import React, { useContext } from 'react';
import { withTheme } from '@emotion/react';
import { TestId } from '@components/test-ids';
import { Entry, TripTypes, VideoGuide } from '@model/contentful';
import { DestinationLinkBuilder, LinkItem } from '@model/iceberg/geography';
import { DestinationPageContext } from '../../providers/context';
import { GuideLink } from '@components/common/next-link';
import { useI18NextContext } from '@components/hooks';
import { Carousel } from '@components/common';
import { Page } from '@model/common';
import { SimpleTile } from '@components/common/simple-tile';

/* ***************** *
 *       Types       *
 * ***************** */

export interface VideoGuideGroupCMSConfig {
  title: string;
}

export interface VideoGuideGroupProps {
  testId?: string;
  destination: any;
  tripType?: TripTypes;
  className?: string;
}

const MAX_NUMBER_OF_TILES = 3;

/* ******************************* *
 *    VideoGuideGroupComponent    *
 * ******************************* */

export const VideoGuideGroupComponent = ({ destination, tripType, className, testId }: VideoGuideGroupProps) => {
  const t: any = useI18NextContext();

  const { cmsVideoGuides }: any = useContext(DestinationPageContext);

  if (!cmsVideoGuides || !cmsVideoGuides.length) return null;

  /* *** RENDERERS *** */
  const renderVideoGuideTiles = () =>
    cmsVideoGuides.map((videoGuide: Entry<VideoGuide>, index: number) => {
      const {
        fields: { title, shortDescription, path, thumbnailImage }
      } = videoGuide;
      return (
        index < MAX_NUMBER_OF_TILES && (
          <GuideLink
            key={path}
            path={destination.destinationPath.fields.path}
            guidePath={path}
            tripType={tripType}
            guidePage={Page.VIDEO_GUIDES}
          >
            <SimpleTile title={title} shortDescription={shortDescription} imageUrl={thumbnailImage.fields.file.url} />
          </GuideLink>
        )
      );
    });

  const builder: DestinationLinkBuilder = new DestinationLinkBuilder();
  const videoGuideLink: LinkItem = builder.getGuidesLink(
    destination.destinationPath.fields.path,
    Page.VIDEO_GUIDES,
    tripType
  );

  const link =
    cmsVideoGuides.length > MAX_NUMBER_OF_TILES
      ? { label: t('video-guides__label--view-more-videos'), linkItem: videoGuideLink }
      : undefined;

  return (
    <Carousel
      className={className}
      testId={testId || TestId.guides.groupComponent}
      title={t('video-guides__page-title--label', { destination: destination.title })}
      link={link}
      slidesToShow={MAX_NUMBER_OF_TILES}
    >
      {renderVideoGuideTiles()}
    </Carousel>
  );
};

export const VideoGuideGroup = withTheme(VideoGuideGroupComponent);
